import { STORAGE } from './constants';
/**
 * Dictionary Manager
 *
 * Handles loading, shuffling, and generating word arrays.
 * Empty difficulty filters fall back to the full dictionary (never zero words).
 */
export class DictionaryManager {
    constructor() {
        this.cache = new Map();
    }
    async loadDictionary(config, forceRefresh = false) {
        const cacheKey = config;
        if (forceRefresh) {
            this.cache.delete(cacheKey);
        }
        else if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }
        const cacheBuster = `?v=${STORAGE.DICTIONARY_CACHE_VERSION}`;
        const url = `/assets/data/typeracer/dictionaries/${config}.json${cacheBuster}`;
        const response = await fetch(url, {
            cache: 'no-store',
        });
        if (!response.ok) {
            throw new Error(`Failed to load dictionary: ${url}`);
        }
        const data = await response.json();
        const dictionary = {
            name: data.name || config,
            language: data.language || 'estonian',
            words: data.words || [],
            weights: data.weights,
            metadata: data.metadata,
        };
        if (!dictionary.words.length) {
            throw new Error(`Dictionary is empty: ${url}`);
        }
        this.cache.set(cacheKey, dictionary);
        return dictionary;
    }
    clearCache() {
        this.cache.clear();
    }
    generateWordArray(count, dictionary, difficulty, isParagraphs = false) {
        let words = [...dictionary.words];
        let notice = null;
        if (difficulty) {
            const filtered = this.filterWordsByDifficulty(words, difficulty, isParagraphs);
            if (filtered.length === 0) {
                // Soft fallback: never return an empty race
                notice = 'Valitud raskusastmega sõnu ei leitud — kasutame kogu sõnastikku.';
                words = [...dictionary.words];
            }
            else {
                words = filtered;
            }
        }
        this.shuffleWords(words);
        if (!isParagraphs) {
            // Cycle the pool if we need more words than available
            const result = [];
            if (words.length === 0) {
                return { words: [], notice: notice || 'Sõnastik on tühi.' };
            }
            for (let i = 0; i < count; i++) {
                result.push(words[i % words.length]);
            }
            return { words: result, notice };
        }
        // Paragraphs mode: each entry is a full paragraph → split into words
        if (words.length === 0) {
            return { words: [], notice: notice || 'Sõnastik on tühi.' };
        }
        const selectedParagraphs = [];
        for (let i = 0; i < count; i++) {
            selectedParagraphs.push(words[i % words.length]);
        }
        const paragraphWords = [];
        selectedParagraphs.forEach((paragraph) => {
            const parts = paragraph
                .split(/\s+/)
                .map((w) => w.trim())
                .filter((w) => w.length);
            paragraphWords.push(...parts);
        });
        return { words: paragraphWords, notice };
    }
    filterWordsByDifficulty(words, difficulty, isParagraphs = false) {
        let difficultyRanges;
        if (isParagraphs) {
            difficultyRanges = {
                easy: { min: 50, max: 200 },
                medium: { min: 201, max: 400 },
                hard: { min: 401, max: 600 },
                impossible: { min: 601, max: Infinity },
            };
        }
        else {
            difficultyRanges = {
                easy: { min: 1, max: 4 },
                medium: { min: 5, max: 8 },
                hard: { min: 9, max: 12 },
                impossible: { min: 13, max: Infinity },
            };
        }
        const range = difficultyRanges[difficulty];
        return words.filter((word) => {
            const length = word.length;
            return length >= range.min && length <= range.max;
        });
    }
    shuffleWords(array) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
    }
}
